/** Immediate SUBMITTING feedback + duplicate-click guard for media Generate nodes. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  logCanvasGenerationTiming,
  markCanvasGenerationTiming,
} from './canvasGenerationSubmissionTiming'

type StatusField = 'status' | 'runStatus'

export function useCanvasGenerationSubmission(args: {
  nodeId: string
  fieldStatus: string
  patchFields: (patch: Record<string, unknown>) => void
  runHydrating?: boolean
  statusField?: StatusField
}) {
  const { nodeId, fieldStatus, patchFields, runHydrating = false, statusField = 'status' } = args
  const [localSubmitting, setLocalSubmitting] = useState(false)
  const submitLockRef = useRef(false)

  const cancelSubmission = useCallback(() => {
    submitLockRef.current = false
    setLocalSubmitting(false)
  }, [])

  const beginSubmission = useCallback(
    (extraPatch?: Record<string, unknown>) => {
      if (submitLockRef.current) return false
      submitLockRef.current = true
      markCanvasGenerationTiming(nodeId, 'T0_CLICK')
      flushSync(() => {
        setLocalSubmitting(true)
      })
      markCanvasGenerationTiming(nodeId, 'T1_VISIBLE_FEEDBACK')
      patchFields({
        [statusField]: 'SUBMITTING',
        needsRegeneration: false,
        ...extraPatch,
      })
      markCanvasGenerationTiming(nodeId, 'T2_FIELDS_PATCHED')
      return true
    },
    [nodeId, patchFields, statusField],
  )

  const markRequestStart = useCallback(() => {
    markCanvasGenerationTiming(nodeId, 'T5_REQUEST_START')
  }, [nodeId])

  const markJobAccepted = useCallback(() => {
    markCanvasGenerationTiming(nodeId, 'T11_JOB_ACCEPTED')
    logCanvasGenerationTiming(nodeId)
    submitLockRef.current = false
    // Keep localSubmitting until backend-owned status (QUEUED+, terminal) lands —
    // avoids a frame where FAILED + !localSubmitting shows stale failure UI.
  }, [nodeId])

  const failSubmission = useCallback(
    (patch?: Record<string, unknown>) => {
      cancelSubmission()
      logCanvasGenerationTiming(nodeId)
      patchFields(patch ?? { [statusField]: 'READY' })
    },
    [cancelSubmission, nodeId, patchFields, statusField],
  )

  // Release the client-side SUBMITTING overlay only once backend-owned status
  // takes over. Never clear on FAILED — retry-from-failed must keep immediate
  // click feedback until QUEUED/GENERATING (or failSubmission/markJobAccepted).
  useEffect(() => {
    if (!localSubmitting) return
    if (
      fieldStatus === 'QUEUED' ||
      fieldStatus === 'WAITING_FOR_ENGINE' ||
      fieldStatus === 'UPLOADING_ASSETS' ||
      fieldStatus === 'SUBMITTING_REQUEST' ||
      fieldStatus === 'RETRYING' ||
      fieldStatus === 'GENERATING' ||
      fieldStatus === 'FAILED' ||
      fieldStatus === 'COMPLETED'
    ) {
      setLocalSubmitting(false)
    }
  }, [fieldStatus, localSubmitting])

  const effectiveStatus = localSubmitting ? 'SUBMITTING' : fieldStatus

  const isBusy =
    runHydrating ||
    localSubmitting ||
    fieldStatus === 'SUBMITTING' ||
    fieldStatus === 'QUEUED' ||
    fieldStatus === 'WAITING_FOR_ENGINE' ||
    fieldStatus === 'UPLOADING_ASSETS' ||
    fieldStatus === 'SUBMITTING_REQUEST' ||
    fieldStatus === 'RETRYING' ||
    fieldStatus === 'GENERATING'

  return {
    beginSubmission,
    cancelSubmission,
    failSubmission,
    markRequestStart,
    markJobAccepted,
    submitLockRef,
    localSubmitting,
    effectiveStatus,
    isBusy,
  }
}
